import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  googleSession: (sessionId) => api.post('/auth/session', { session_id: sessionId }),
};

// Profile API
export const profileAPI = {
  get: () => api.get('/profiles/'),
  create: (data) => api.post('/profiles/', data),
  update: (data) => api.put('/profiles/', data),
  getById: (userId) => api.get(`/profiles/${userId}`),
  uploadPhoto: (formData) => api.post('/profiles/upload-photo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deletePhoto: (filename) => api.delete(`/profiles/photo/${filename}`),
};

// Discovery/Swipes API
export const swipeAPI = {
  discover: (filters) => api.get('/swipes/discover', { params: filters }),
  action: (data) => api.post('/swipes/action', data),
  getSettings: () => api.get('/swipes/settings'),
  updateSettings: (data) => api.post('/swipes/settings', data),
  getStatus: () => api.get('/swipes/status'),
};

// Settings API
export const settingsAPI = {
  // Profile settings
  getProfile: () => api.get('/settings/profile'),
  updateProfile: (data) => api.patch('/settings/profile', data),
  
  // Account settings
  getAccount: () => api.get('/settings/account'),
  updateAccount: (data) => api.patch('/settings/account', data),
  
  // Visibility & Pause
  setVisibility: (isVisible) => api.post('/settings/visibility', { is_visible: isVisible }),
  pauseAccount: (data) => api.post('/settings/pause', data),
  
  // Account deletion
  deleteAccount: (data) => api.post('/settings/delete', data),
  
  // Membership
  getMembership: () => api.get('/settings/membership'),
  downgrade: () => api.post('/settings/membership/downgrade'),
  
  // Photos
  getPhotos: () => api.get('/settings/photos'),
  reorderPhotos: (photoUrls) => api.post('/settings/photos/reorder', { photo_urls: photoUrls }),
  setMainPhoto: (photoUrl) => api.post('/settings/photos/main', { photo_url: photoUrl }),
  
  // Logout all
  logoutAll: () => api.post('/settings/logout-all'),
};

// Matches API
export const matchAPI = {
  getAll: () => api.get('/matches/'),
};

// Messages API
export const messageAPI = {
  get: (matchId) => api.get(`/messages/${matchId}`),
  send: (data) => api.post('/messages/', data),
  getUnreadCount: () => api.get('/messages/unread/count'),
  getConversations: () => api.get('/messages/conversations'),
  sendTyping: (matchId) => api.post(`/messages/typing/${matchId}`),
  markRead: (matchId) => api.post(`/messages/read/${matchId}`),
};

// Translation API
export const translateAPI = {
  translate: (data) => api.post('/translate/', data),
};

// Payments API
export const paymentAPI = {
  // Stripe
  checkout: (data) => api.post('/payments/checkout', data),
  createSubscription: (data) => api.post('/payments/create-subscription', data),
  status: (sessionId) => api.get(`/payments/status/${sessionId}`),
  getSubscription: () => api.get('/payments/subscription'),
  cancelSubscription: () => api.post('/payments/cancel-subscription'),
  // PayPal (NVP API)
  paypalCreateSubscription: (data) => api.post('/payments/paypal/create-subscription', data),
  paypalExecute: (token, PayerID) => api.get(`/payments/paypal/execute?token=${token}&PayerID=${PayerID}`),
  paypalStatus: (token) => api.get(`/payments/paypal/status/${token}`),
  // Methods
  getPaymentMethods: () => api.get('/payments/methods'),
};

// Content API
export const contentAPI = {
  successStories: () => api.get('/success-stories'),
  countryGuides: () => api.get('/country-guides'),
  safetyTips: () => api.get('/safety-tips'),
};

// Moderation API
export const moderationAPI = {
  reportUser: (data) => api.post('/moderation/report', data),
  blockUser: (data) => api.post('/moderation/block', data),
  unblockUser: (userId) => api.post(`/moderation/unblock/${userId}`),
  getBlockedUsers: () => api.get('/moderation/blocked'),
};

// Premium Features API
export const premiumAPI = {
  getWhoLikedMe: () => api.get('/swipes/likes-received'),
  boostProfile: () => api.post('/profiles/boost'),
};

// Admin API
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getUsers: (params) => api.get('/admin/users', { params }),
  userAction: (data) => api.post('/admin/users/action', data),
  getReports: (params) => api.get('/admin/reports', { params }),
  handleReport: (reportId, action) => api.post(`/admin/reports/${reportId}/action?action=${action}`),
};

export default api;
